/**
 * React Query mutation hook for POST /api/company-sourcing/analyze.
 * Mirrors the useCompanyNewsAnalysis hook — on-demand call cached in
 * QueryClient keyed by (company_name, days_back).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../lib/api';
import type {
  DaysBackOption,
  SourcingAnalysisRequest,
  SourcingAnalysisResponse,
} from './types';

const QUERY_KEY = ['company-sourcing', 'analyze'] as const;
const STALE_TIME_MS = 1000 * 60 * 60 * 12; // 12 h — matches backend Mongo/Neo4j TTL

interface CacheEntry {
  data: SourcingAnalysisResponse;
  fetchedAt: number;
}

const cacheKey = (companyName: string, daysBack: number) =>
  `${companyName.trim().toLowerCase()}::${daysBack}`;

async function postAnalyze(
  req: SourcingAnalysisRequest
): Promise<SourcingAnalysisResponse> {
  const { data } = await apiClient.post<SourcingAnalysisResponse>(
    '/company-sourcing/article-signals',
    req,
    { timeout: 180_000 }
  );
  return data;
}

export interface UseCompanySourcingAnalysisOptions {
  companyName: string | null | undefined;
  defaultDaysBack?: DaysBackOption;
  defaultMaxArticles?: number;
  autoFetch?: boolean;
}

export interface UseCompanySourcingAnalysisResult {
  data: SourcingAnalysisResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  daysBack: DaysBackOption;
  setDaysBack: (value: DaysBackOption) => void;
  refresh: () => void;
  fetchedAt: number | null;
}

export function useCompanySourcingAnalysis({
  companyName,
  defaultDaysBack = 90,
  // No defaultMaxArticles override — let the backend's Pydantic default (20)
  // win. If we hardcode a value here, every change to the backend default
  // gets ignored until the frontend is also re-deployed.
  defaultMaxArticles,
  autoFetch = true,
}: UseCompanySourcingAnalysisOptions): UseCompanySourcingAnalysisResult {
  const queryClient = useQueryClient();
  const [daysBack, setDaysBack] = useState<DaysBackOption>(defaultDaysBack);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const inflightKeyRef = useRef<string | null>(null);

  const mutation = useMutation<SourcingAnalysisResponse, Error, SourcingAnalysisRequest>({
    mutationFn: postAnalyze,
    onSettled: () => {
      inflightKeyRef.current = null;
    },
    onSuccess: (resp, variables) => {
      const now = Date.now();
      setFetchedAt(now);
      queryClient.setQueryData<CacheEntry>(
        [...QUERY_KEY, cacheKey(variables.company_name ?? '', variables.days_back)],
        { data: resp, fetchedAt: now }
      );
    },
  });

  const trigger = useCallback(
    (force = false) => {
      if (!companyName) return;
      const reqKey = cacheKey(companyName, daysBack);
      if (!force && inflightKeyRef.current === reqKey) return;
      const key = [...QUERY_KEY, reqKey];
      const cached = queryClient.getQueryData<CacheEntry>(key);
      if (!force && cached && Date.now() - cached.fetchedAt < STALE_TIME_MS) {
        setFetchedAt(cached.fetchedAt);
        return;
      }
      inflightKeyRef.current = reqKey;
      // Only include max_articles if the caller explicitly provided one;
      // otherwise the backend's Pydantic default applies (currently 20).
      const body: SourcingAnalysisRequest = {
        company_name: companyName,
        days_back: daysBack,
      };
      if (defaultMaxArticles !== undefined) {
        body.max_articles = defaultMaxArticles;
      }
      mutation.mutate(body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyName, daysBack, queryClient]
  );

  useEffect(() => {
    if (!autoFetch || !companyName) return;
    const id = setTimeout(() => trigger(false), 0);
    return () => clearTimeout(id);
  }, [autoFetch, companyName, daysBack, trigger]);

  const cached = companyName
    ? queryClient.getQueryData<CacheEntry>([
        ...QUERY_KEY,
        cacheKey(companyName, daysBack),
      ])
    : undefined;

  const data = cached?.data ?? mutation.data ?? null;

  return {
    data,
    isLoading: mutation.isPending && !data,
    isRefreshing: mutation.isPending && !!data,
    error: mutation.error ? mutation.error.message : null,
    daysBack,
    setDaysBack,
    refresh: () => trigger(true),
    fetchedAt: cached?.fetchedAt ?? fetchedAt,
  };
}
